<template>
  <div class="dossier-outsourcing">
    <!-- ─── Card wrapper: pergunta condicional ─── -->
    <!-- Não usa DossierDocumentCard aqui — o wrapper é próprio porque
         este módulo contém 3 cards filhos independentes, não um único documento. -->
    <div class="dossier-outsourcing__wrapper">
      <!-- Cabeçalho do módulo -->
      <div class="dossier-outsourcing__header" @click="toggleCollapse">
        <div class="dossier-outsourcing__header-left">
          <span
            class="dossier-outsourcing__state-icon"
            :class="headerStateClass"
          >
            <i :class="headerStateIcon"></i>
          </span>
          <div>
            <h3 class="dossier-outsourcing__title">
              Terceirização de Serviços
            </h3>
            <span v-if="isCollapsed" class="dossier-outsourcing__subtitle">
              Alimentação · Limpeza · Lavanderia
            </span>
          </div>
        </div>
        <button
          class="dossier-outsourcing__toggle"
          type="button"
          :aria-expanded="!isCollapsed"
        >
          <i
            class="fas"
            :class="isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
          ></i>
        </button>
      </div>

      <!-- Corpo colapsável -->
      <div v-show="!isCollapsed" class="dossier-outsourcing__body">
        <!-- Texto explicativo (só no modo edição) -->
        <div v-if="!isViewMode" class="dossier-outsourcing__explainer">
          <p>
            Registre os contratos de terceirização dos serviços de
            <strong>alimentação</strong>,
            <strong>limpeza/higienização</strong> e
            <strong>lavanderia</strong>.<br />
            Cada serviço é independente — preencha apenas os que a instituição
            terceiriza. Serviços executados pela própria equipe não precisam ser
            registrados aqui.
          </p>
        </div>

        <!-- ── Pergunta condicional ── -->
        <div class="dossier-conditional__question">
          <label class="dossier-conditional__question-label">
            <i class="fas fa-question-circle mr-1"></i>
            A instituição terceiriza algum destes serviços?
          </label>

          <div class="dossier-conditional__options">
            <label class="dossier-conditional__option">
              <input
                type="radio"
                value="sim"
                v-model="formData.is_applicable_answer"
                :disabled="isViewMode"
                class="dossier-conditional__radio"
              />
              <span>Sim — terceiriza pelo menos um serviço</span>
            </label>
            <label class="dossier-conditional__option">
              <input
                type="radio"
                value="nao"
                v-model="formData.is_applicable_answer"
                :disabled="isViewMode"
                class="dossier-conditional__radio"
              />
              <span
                >Não — todos os serviços são executados pela própria
                equipe</span
              >
            </label>
          </div>

          <div
            v-if="formData.is_applicable_answer === 'nao'"
            class="dossier-conditional__na-message"
          >
            <i class="fas fa-info-circle mr-1"></i>
            Nenhum contrato de terceirização precisa ser registrado. Este módulo
            não gera pendência para a instituição.
          </div>
        </div>

        <!-- ── Sub-blocos de serviço (quando aplicável) ── -->
        <template v-if="formData.is_applicable_answer === 'sim'">
          <!-- Seleção de serviços terceirizados -->
          <div v-if="!isViewMode" class="dossier-outsourcing__services-select">
            <label class="dossier-outsourcing__services-label">
              Quais serviços são terceirizados?
            </label>
            <div class="dossier-outsourcing__services-checkboxes">
              <label
                v-for="service in serviceOptions"
                :key="service.key"
                class="dossier-outsourcing__service-checkbox"
              >
                <input
                  type="checkbox"
                  :value="service.key"
                  v-model="formData.active_services"
                  class="dossier-outsourcing__checkbox"
                />
                <i :class="service.icon" class="mr-1"></i>
                <span>{{ service.label }}</span>
              </label>
            </div>
          </div>

          <!-- ── Card: Alimentação ── -->
          <div
            v-show="isServiceActive('food')"
            class="dossier-outsourcing__service-block"
          >
            <div class="dossier-outsourcing__service-label">
              <i class="fas fa-utensils mr-1"></i>
              Alimentação
            </div>
            <DossierDocumentCard
              title="Contrato de Terceirização – Alimentação"
              :status-options="contractStatusOptions"
              :multiple-status="true"
              :status-requires-validity="[
                'possui_vigente',
                'possui_provisorio',
              ]"
              :status-requires-upload="['possui_vigente', 'possui_provisorio']"
              explainer-text=""
              :mode="mode"
              :not-applicable="false"
              :start-collapsed="false"
              :is_applicable_answer="formData.is_applicable_answer"
              type="food"
              :active-services="formData.active_services"
              v-model="formData.food"
              save-endpoint="/rest-home/dossier/outsourcing/food"
              upload-endpoint="/rest-home/dossier/outsourcing/food/upload"
              @saved="(d) => onServiceSaved('food', d)"
              @error="(e) => onServiceError('food', e)"
            >
              <template #fields-before>
                <div class="dossier-module__fields">
                  <div class="dossier-module__field-group">
                    <label class="dossier-module__label"
                      >Empresa contratada</label
                    >
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.food.company_name"
                      dense
                      outlined
                      hide-details
                      placeholder="Nome da empresa"
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.food.company_name || "—"
                    }}</span>
                  </div>
                  <div
                    class="dossier-module__field-group dossier-module__field-group--small"
                  >
                    <label class="dossier-module__label">CNPJ da empresa</label>
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.food.company_cnpj"
                      v-mask="'##.###.###/####-##'"
                      dense
                      outlined
                      hide-details
                      placeholder="00.000.000/0000-00"
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.food.company_cnpj || "—"
                    }}</span>
                  </div>
                </div>
              </template>
            </DossierDocumentCard>
          </div>

          <!-- ── Card: Limpeza / Higienização ── -->
          <div
            v-show="isServiceActive('cleaning')"
            class="dossier-outsourcing__service-block"
          >
            <div class="dossier-outsourcing__service-label">
              <i class="fas fa-broom mr-1"></i>
              Limpeza / Higienização
            </div>
            <DossierDocumentCard
              title="Contrato de Terceirização – Limpeza / Higienização"
              :status-options="contractStatusOptions"
              :multiple-status="true"
              :status-requires-validity="[
                'possui_vigente',
                'possui_provisorio',
              ]"
              :status-requires-upload="['possui_vigente', 'possui_provisorio']"
              explainer-text=""
              :mode="mode"
              :not-applicable="false"
              :start-collapsed="false"
              :is_applicable_answer="formData.is_applicable_answer"
              type="cleaning"
              :active-services="formData.active_services"
              v-model="formData.cleaning"
              save-endpoint="/rest-home/dossier/outsourcing/cleaning"
              upload-endpoint="/rest-home/dossier/outsourcing/cleaning/upload"
              @saved="(d) => onServiceSaved('cleaning', d)"
              @error="(e) => onServiceError('cleaning', e)"
            >
              <template #fields-before>
                <div class="dossier-module__fields">
                  <div class="dossier-module__field-group">
                    <label class="dossier-module__label"
                      >Empresa contratada</label
                    >
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.cleaning.company_name"
                      dense
                      outlined
                      hide-details
                      placeholder="Nome da empresa"
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.cleaning.company_name || "—"
                    }}</span>
                  </div>
                  <div
                    class="dossier-module__field-group dossier-module__field-group--small"
                  >
                    <label class="dossier-module__label">CNPJ da empresa</label>
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.cleaning.company_cnpj"
                      v-mask="'##.###.###/####-##'"
                      dense
                      outlined
                      hide-details
                      placeholder="00.000.000/0000-00"
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.cleaning.company_cnpj || "—"
                    }}</span>
                  </div>
                </div>
              </template>
            </DossierDocumentCard>
          </div>

          <!-- ── Card: Lavanderia ── -->
          <div
            v-show="isServiceActive('laundry')"
            class="dossier-outsourcing__service-block"
          >
            <div class="dossier-outsourcing__service-label">
              <i class="fas fa-tshirt mr-1"></i>
              Lavanderia
            </div>
            <DossierDocumentCard
              title="Contrato de Terceirização – Lavanderia"
              :status-options="contractStatusOptions"
              :multiple-status="true"
              :status-requires-validity="[
                'possui_vigente',
                'possui_provisorio',
              ]"
              :status-requires-upload="['possui_vigente', 'possui_provisorio']"
              explainer-text=""
              :mode="mode"
              :not-applicable="false"
              :start-collapsed="false"
              :is_applicable_answer="formData.is_applicable_answer"
              type="laundry"
              :active-services="formData.active_services"
              v-model="formData.laundry"
              save-endpoint="/rest-home/dossier/outsourcing/laundry"
              upload-endpoint="/rest-home/dossier/outsourcing/laundry/upload"
              @saved="(d) => onServiceSaved('laundry', d)"
              @error="(e) => onServiceError('laundry', e)"
            >
              <template #fields-before>
                <div class="dossier-module__fields">
                  <div class="dossier-module__field-group">
                    <label class="dossier-module__label"
                      >Empresa contratada</label
                    >
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.laundry.company_name"
                      dense
                      outlined
                      hide-details
                      placeholder="Nome da empresa"
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.laundry.company_name || "—"
                    }}</span>
                  </div>
                  <div
                    class="dossier-module__field-group dossier-module__field-group--small"
                  >
                    <label class="dossier-module__label">CNPJ da empresa</label>
                    <v-text-field
                      v-if="!isViewMode"
                      v-model="formData.laundry.company_cnpj"
                      v-mask="'##.###.###/####-##'"
                      dense
                      outlined
                      hide-details
                      placeholder="00.000.000/0000-00"
                      class="dossier-module__input"
                    />
                    <span v-else class="dossier-module__view-value">{{
                      formData.laundry.company_cnpj || "—"
                    }}</span>
                  </div>
                </div>
              </template>
            </DossierDocumentCard>
          </div>

          <!-- Estado vazio: nenhum serviço selecionado -->
          <div
            v-if="formData.active_services.length === 0 && !isViewMode"
            class="dossier-outsourcing__empty"
          >
            <i class="fas fa-info-circle mr-1"></i>
            Selecione acima quais serviços são terceirizados para preencher os
            contratos.
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";

// Estrutura vazia padrão de cada sub-bloco de serviço
const emptyService = () => ({
  company_name: "",
  company_cnpj: "",
  status: [],
  validity_date: null,
  file_url: null,
  file_name: null,
  observations: "",
});

export default {
  name: "DossierOutsourcing",
  components: { DossierDocumentCard },

  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },

  data() {
    const d = this.initialData || {};
    return {
      isCollapsed: this.startCollapsed,

      serviceOptions: [
        { key: "food", label: "Alimentação", icon: "fas fa-utensils" },
        {
          key: "cleaning",
          label: "Limpeza / Higienização",
          icon: "fas fa-broom",
        },
        { key: "laundry", label: "Lavanderia", icon: "fas fa-tshirt" },
      ],

      contractStatusOptions: [
        { value: "possui_vigente", label: "Possui contrato vigente" },
        { value: "possui_provisorio", label: "Possui contrato provisório" },
        { value: "em_elaboracao", label: "Contrato em elaboração" },
        { value: "nao_possui", label: "Não possui (pendente)" },
      ],

      formData: {
        // Resposta da pergunta condicional
        is_applicable_answer: d.is_applicable_answer || null,

        // Serviços marcados como terceirizados
        active_services: d.active_services || [],

        // Dados de cada sub-bloco (independentes)
        food: { ...emptyService(), ...(d.food || {}) },
        cleaning: { ...emptyService(), ...(d.cleaning || {}) },
        laundry: { ...emptyService(), ...(d.laundry || {}) },
      },
    };
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    // Estado geral do módulo para o ícone do cabeçalho
    // 'ok' se todos os serviços ativos têm contrato | 'pending' se algum falta | 'na' se não terceiriza
    headerState() {
      if (this.formData.is_applicable_answer === "nao") return "na";
      if (!this.formData.is_applicable_answer) return "pending";
      if (this.formData.active_services.length === 0) return "pending";

      const allOk = this.formData.active_services.every((key) => {
        const sub = this.formData[key];
        return sub && sub.status && sub.status.length > 0;
      });
      return allOk ? "ok" : "pending";
    },

    headerStateClass() {
      const map = {
        ok: "dossier-outsourcing__state-icon--ok",
        pending: "dossier-outsourcing__state-icon--pending",
        na: "dossier-outsourcing__state-icon--na",
      };
      return map[this.headerState] || "";
    },

    headerStateIcon() {
      const map = {
        ok: "fas fa-check-circle",
        pending: "far fa-circle",
        na: "fas fa-minus-circle",
      };
      return map[this.headerState] || "far fa-circle";
    },
  },

  watch: {
    // Quando desmarca um serviço, limpa os dados do sub-bloco correspondente
    "formData.active_services"(newVal, oldVal) {
      const removed = oldVal.filter((k) => !newVal.includes(k));
      removed.forEach((key) => {
        this.formData[key] = emptyService();
      });
    },

    // Emite para o pai sempre que algo mudar
    formData: {
      deep: true,
      handler(newVal) {
        this.$emit("input", JSON.parse(JSON.stringify(newVal)));
        this.$emit("change", { module: "outsourcing", data: newVal });
      },
    },

    initialData: {
      deep: true,
      handler(d) {
        this.formData.is_applicable_answer = d.is_applicable_answer || null;
        this.formData.active_services = d.active_services || [];
        this.formData.food = { ...emptyService(), ...(d.food || {}) };
        this.formData.cleaning = { ...emptyService(), ...(d.cleaning || {}) };
        this.formData.laundry = { ...emptyService(), ...(d.laundry || {}) };
      },
    },
  },

  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    isServiceActive(key) {
      return this.formData.active_services.includes(key);
    },

    onServiceSaved(serviceKey, data) {
      this.$emit("saved", { module: "outsourcing", service: serviceKey, data });
    },

    onServiceError(serviceKey, errors) {
      this.$emit("error", {
        module: "outsourcing",
        service: serviceKey,
        errors,
      });
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER TERCEIRIZAÇÃO
   Wrapper próprio com cabeçalho colapsável + 3 DossierDocumentCards.
   ═══════════════════════════════════════════════════════════════ */

/* ── Wrapper externo ─────────────────────────────────────────── */
.dossier-outsourcing {
  margin-bottom: 12px;
}

.dossier-outsourcing__wrapper {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  transition: box-shadow 0.2s;
}

.dossier-outsourcing__wrapper:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* ── Cabeçalho ───────────────────────────────────────────────── */
.dossier-outsourcing__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
}

.dossier-outsourcing__header:hover {
  background: #f0f0ef;
}

.dossier-outsourcing__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dossier-outsourcing__title {
  font-size: 14px;
  font-weight: 600;
  color: #33495e;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dossier-outsourcing__subtitle {
  font-size: 12px;
  color: #888;
  display: block;
  margin-top: 2px;
}

/* ── Ícone de estado ─────────────────────────────────────────── */
.dossier-outsourcing__state-icon {
  font-size: 15px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.dossier-outsourcing__state-icon--ok {
  color: #30915b;
}
.dossier-outsourcing__state-icon--pending {
  color: #ccc;
}
.dossier-outsourcing__state-icon--na {
  color: #bbb;
}

/* ── Botão toggle ────────────────────────────────────────────── */
.dossier-outsourcing__toggle {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #888;
  font-size: 13px;
}

.dossier-outsourcing__toggle:hover {
  color: #1f5a76;
}

/* ── Corpo ───────────────────────────────────────────────────── */
.dossier-outsourcing__body {
  padding: 16px;
}

/* ── Texto explicativo ───────────────────────────────────────── */
.dossier-outsourcing__explainer {
  font-size: 13px;
  color: #555;
  background: #f8f8f7;
  border-left: 3px solid #1f5a76;
  border-radius: 0 4px 4px 0;
  padding: 10px 12px;
  margin-bottom: 14px;
  line-height: 1.6;
}

.dossier-outsourcing__explainer p {
  margin: 0;
}

/* ── Seleção de serviços ─────────────────────────────────────── */
.dossier-outsourcing__services-select {
  margin-bottom: 16px;
}

.dossier-outsourcing__services-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
}

.dossier-outsourcing__services-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
}

.dossier-outsourcing__service-checkbox {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  font-weight: 500;
}

.dossier-outsourcing__checkbox {
  accent-color: #1f5a76;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

/* ── Sub-bloco de serviço ────────────────────────────────────── */
.dossier-outsourcing__service-block {
  margin-bottom: 8px;
}

/* Label acima do card filho */
.dossier-outsourcing__service-label {
  font-size: 12px;
  font-weight: 600;
  color: #1f5a76;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
  padding-left: 2px;
}

/* Recuo visual dos cards filhos */
.dossier-outsourcing__service-block :deep(.dossier-card) {
  border-color: #b8d9ec;
  border-left: 3px solid #1f5a76;
}

/* ── Estado vazio ────────────────────────────────────────────── */
.dossier-outsourcing__empty {
  font-size: 13px;
  color: #888;
  font-style: italic;
  padding: 12px;
  border: 1px dashed #ddd;
  border-radius: 4px;
  text-align: center;
  margin-top: 8px;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-outsourcing__services-checkboxes {
    flex-direction: column;
    gap: 10px;
  }

  .dossier-outsourcing__body {
    padding: 12px;
  }
}
</style>